import { useWorkspaceV2 } from './useWorkspaceV2';
import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useOptimisticSetting } from 'dashboard/composables/useOptimisticSetting';

// SiteUp — layout único (ex-Modo Foco). A barra lateral saiu. Estado em ui_settings:
//   focus_mode_enabled        → legado: ignorado. O layout é sempre o focado
//   focus_mode_default_v2     → migração única: aplica lista/painel uma vez
//   focus_mode_list_default_v3 → migração única: lista de conversas volta a ser
//                                visível por padrão. A imersão passa a ser
//                                escolha explícita no cluster arrastável.
//   focus_mode_onboarded      → legado: já viu o card antigo (migra p/ guide)
//   focus_mode_guide          → { welcome_seen, tour_completed, tour_last_step, entries_count, nav_v2_seen }
//   focus_mode_dock_position  → { xPct, yPct } do cluster (viewport, 0–1)
//   focus_mode_dock_collision_v4 → migração única: se a posição salva
//     (ainda da era horizontal) passa a tapar cartão/fila/busca do hub
//     com o cluster vertical, volta ao canto. Quem não cola fica.
//   focus_mode_menu_side      → auto | up | down | left | right
//     Decide só o lado do MENU. A forma do cluster é sempre vertical.
export const FOCUS_MODE_DEFAULT_V2_KEY = 'focus_mode_default_v2';
export const FOCUS_LIST_DEFAULT_V3_KEY = 'focus_mode_list_default_v3';
export const FOCUS_DOCK_COLLISION_V4_KEY = 'focus_mode_dock_collision_v4';

// Medido no staging 1440x900 depois do PR 490 (cluster vertical).
export const FOCUS_CLUSTER_SIZE = Object.freeze({ width: 54, height: 210 });
export const FOCUS_HUB_DESKTOP_MIN_WIDTH = 1024;

const HUB_MAX_WIDTH = 1180;
const HUB_PAD_X = 24;
const HUB_PAD_X_NARROW = 20;
const HUB_PAD_Y_MIN = 38;
const HUB_PAD_Y_MAX = 76;
// Medido no staging em 1440x900: a saudacao ocupa 80px (corpo grande), e o
// cartao da segunda fileira 252px. Com os valores antigos (44 e 204) a area
// dos cartoes terminava 84px acima do real, e quem tinha o cluster salvo
// naquela faixa nao era migrado. Ver spec/geometriaReal.spec.js.
const HUB_GREET_MIN_H = 80;
const HUB_SEARCH_MIN_H = 44;
const HUB_SECTION_GAP = 28;
const HUB_GRID_GAP = 14;
const HUB_FEATURED_MIN_H = 250;
const HUB_CARD_MIN_H = 252;
const HUB_QUEUE_MIN_W = 260;

const clampHub = (n, min, max) => Math.min(max, Math.max(min, n));

export function canEvaluateDockCollision(viewport) {
  return Boolean(
    viewport &&
      viewport.width >= FOCUS_HUB_DESKTOP_MIN_WIDTH &&
      viewport.height > 0
  );
}

export function dockRectFromPercent(pos, viewport, size = FOCUS_CLUSTER_SIZE) {
  const left = pos.xPct * viewport.width;
  const top = pos.yPct * viewport.height;
  return {
    left,
    top,
    right: left + size.width,
    bottom: top + size.height,
    width: size.width,
    height: size.height,
  };
}

export function hubObstacleRects(viewport) {
  if (!canEvaluateDockCollision(viewport)) return null;

  const padX = viewport.width >= 640 ? HUB_PAD_X : HUB_PAD_X_NARROW;
  const contentWidth = Math.min(
    HUB_MAX_WIDTH,
    Math.max(0, viewport.width - padX * 2)
  );
  const left = (viewport.width - contentWidth) / 2;
  const right = left + contentWidth;
  const padY = clampHub(viewport.width * 0.06, HUB_PAD_Y_MIN, HUB_PAD_Y_MAX);
  const greetBottom = padY + HUB_GREET_MIN_H;
  const searchTop = greetBottom + HUB_SECTION_GAP;
  const searchBottom = searchTop + HUB_SEARCH_MIN_H;
  const gridTop = searchBottom + HUB_SECTION_GAP;
  const gridBottom =
    gridTop + HUB_FEATURED_MIN_H + HUB_GRID_GAP + HUB_CARD_MIN_H;

  return [
    { left: right - HUB_QUEUE_MIN_W, top: padY, right, bottom: greetBottom },
    { left, top: searchTop, right, bottom: searchBottom },
    { left, top: gridTop, right, bottom: gridBottom },
  ];
}

const rectsIntersect = (a, b) =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

export function dockCollidesWithHub(pos, viewport) {
  const obstacles = hubObstacleRects(viewport);
  if (!obstacles) return false;
  const dock = dockRectFromPercent(pos, viewport);
  return obstacles.some(obs => rectsIntersect(dock, obs));
}

const readViewport = () => {
  if (typeof window === 'undefined') return null;
  return { width: window.innerWidth, height: window.innerHeight };
};

export const FOCUS_MENU_SIDES = ['auto', 'up', 'down', 'left', 'right'];
export const FOCUS_MENU_OPPOSITE = {
  up: 'down',
  down: 'up',
  left: 'right',
  right: 'left',
};

// Fallback só quando o menu ainda não foi medido (cluster w-56 ~224px).
// Nunca use isto no lugar da medida real depois do mount.
export const FOCUS_MENU_DEFAULT_SIZE = { width: 224, height: 320 };
// Espaço mínimo para o menu ainda ser usável com rolagem (não estourar).
const MENU_MIN_USABLE_PX = 120;
const MENU_SIDES = ['up', 'down', 'right', 'left'];

const positivePx = (value, fallback) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : fallback;

const normalizeMenuSize = (menuSize = {}) => ({
  width: positivePx(menuSize.width, FOCUS_MENU_DEFAULT_SIZE.width),
  height: positivePx(menuSize.height, FOCUS_MENU_DEFAULT_SIZE.height),
});

export const spacesAround = (rect, viewport, gap = 8) => ({
  up: rect.top - gap,
  down: viewport.height - rect.bottom - gap,
  left: rect.left - gap,
  right: viewport.width - rect.right - gap,
});

// up/down comparam com a ALTURA do menu; left/right com a LARGURA.
const neededSpace = (side, size) =>
  side === 'up' || side === 'down' ? size.height : size.width;

const sideWithMostSpace = spaces =>
  [...MENU_SIDES].sort((a, b) => spaces[b] - spaces[a])[0];

// Colado numa borda: abre para dentro, preferindo ficar na faixa da borda
// (no topo, esquerda/direita; na lateral, cima/baixo) em vez de atravessar
// a área de trabalho. No meio da tela: o lado com mais espaço livre.
// Se nenhum lado cabe por completo, devolve o de mais espaço: o caller
// aplica max-height e o menu rola em vez de sair da tela.
const pickAutoMenuSide = (rect, viewport, spaces, fitsFully) => {
  const midX = (rect.left + rect.right) / 2;
  const midY = (rect.top + rect.bottom) / 2;
  const nearTop = midY < viewport.height / 3;
  const nearBottom = midY > (viewport.height * 2) / 3;
  const nearLeft = midX < viewport.width / 3;
  const nearRight = midX > (viewport.width * 2) / 3;

  const candidates = [];
  if (nearTop || nearBottom) {
    if (nearRight) candidates.push('left');
    else if (nearLeft) candidates.push('right');
    else candidates.push(spaces.right >= spaces.left ? 'right' : 'left');
    candidates.push(nearTop ? 'down' : 'up');
  } else if (nearLeft || nearRight) {
    candidates.push(nearLeft ? 'right' : 'left');
    candidates.push(spaces.down >= spaces.up ? 'down' : 'up');
  } else {
    // Meio da tela: o lado com mais espaço livre (comportamento histórico).
    return sideWithMostSpace(spaces);
  }

  return candidates.find(fitsFully) || sideWithMostSpace(spaces);
};

/**
 * Decide o lado do menu com o tamanho REAL do menu (não uma constante).
 *
 * @param {string} preferred auto | up | down | left | right
 * @param {DOMRect|object} rect retângulo do gatilho/cluster
 * @param {{width:number,height:number}} viewport
 * @param {{width?:number,height?:number}} [menuSize] medida do menu
 */
export function resolveFocusMenuSide(preferred, rect, viewport, menuSize = {}) {
  const size = normalizeMenuSize(menuSize);
  const spaces = spacesAround(rect, viewport);
  const fitsFully = side => spaces[side] >= neededSpace(side, size);
  const fitsUsable = side => spaces[side] >= MENU_MIN_USABLE_PX;

  if (preferred !== 'auto' && FOCUS_MENU_OPPOSITE[preferred]) {
    // Preferência fixa: vale se o menu cabe inteiro, ou se ainda há espaço
    // útil para rolar E o oposto não resolve melhor sozinho.
    if (fitsFully(preferred)) return preferred;

    if (fitsUsable(preferred)) {
      const opposite = FOCUS_MENU_OPPOSITE[preferred];
      if (!fitsFully(opposite) || spaces[preferred] >= spaces[opposite]) {
        return preferred;
      }
    }
    // Preferido não cabe nem com rolagem razoável, ou o oposto cabe
    // inteiro e o preferido não: cai no automático (nunca estoura).
  }

  return pickAutoMenuSide(rect, viewport, spaces, fitsFully);
}

/** Espaço livre no lado escolhido (px). Usado para max-height com rolagem. */
export function focusMenuSpaceOnSide(side, rect, viewport, gap = 8) {
  const spaces = spacesAround(rect, viewport, gap);
  return Math.max(0, spaces[side] || 0);
}

export function useFocusMode() {
  const { isWorkspaceV2 } = useWorkspaceV2();
  const { uiSettings, updateUISettings } = useUISettings();
  const clusterCollapsed = useOptimisticSetting(
    computed(() => uiSettings.value?.focus_mode_cluster_collapsed === true),
    next => updateUISettings({ focus_mode_cluster_collapsed: next === true })
  );

  // true | false | undefined (nunca configurou — default é ligado em qualquer tela)
  const focusModeSetting = computed(() => {
    const value = uiSettings.value?.focus_mode_enabled;
    return typeof value === 'boolean' ? value : undefined;
  });

  // Sempre ligado: focus_mode_enabled: false no ui_settings é ignorado.
  const isFocusModeEnabled = computed(() => true);

  const isFocusModeOnboarded = computed(
    () => uiSettings.value?.focus_mode_onboarded === true
  );

  const focusModeGuide = computed(() => {
    const raw = uiSettings.value?.focus_mode_guide;
    const onboarded = uiSettings.value?.focus_mode_onboarded === true;
    const fromStore = raw && typeof raw === 'object' ? raw : {};
    const lastStep = Number(fromStore.tour_last_step);
    const entries = Number(fromStore.entries_count);
    return {
      welcome_seen: fromStore.welcome_seen === true || onboarded,
      tour_completed: fromStore.tour_completed === true,
      tour_last_step: Number.isFinite(lastStep) ? lastStep : 0,
      entries_count: Number.isFinite(entries) ? entries : 0,
      nav_v2_seen: fromStore.nav_v2_seen === true,
    };
  });

  const shouldShowWelcome = computed(() => !focusModeGuide.value.nav_v2_seen);

  const patchFocusGuide = partial => {
    updateUISettings({
      focus_mode_guide: { ...focusModeGuide.value, ...partial },
      ...(partial.welcome_seen ? { focus_mode_onboarded: true } : {}),
    });
  };

  const markWelcomeSeen = () => {
    if (focusModeGuide.value.welcome_seen) return;
    patchFocusGuide({ welcome_seen: true });
  };

  const markTourCompleted = () => {
    patchFocusGuide({
      welcome_seen: true,
      tour_completed: true,
      nav_v2_seen: true,
    });
  };

  const markNavV2Seen = () => {
    if (focusModeGuide.value.nav_v2_seen) return;
    patchFocusGuide({
      welcome_seen: true,
      nav_v2_seen: true,
    });
  };

  const saveTourStep = step => {
    patchFocusGuide({ tour_last_step: step });
  };

  const restartFocusGuide = () => {
    patchFocusGuide({
      welcome_seen: true,
      tour_completed: false,
      tour_last_step: 0,
      nav_v2_seen: false,
    });
  };

  const applyFocusModeDefaultV2 = () => {
    if (isWorkspaceV2.value) return;
    if (uiSettings.value?.[FOCUS_MODE_DEFAULT_V2_KEY]) return;
    const currentNav = uiSettings.value?.sidebar_width;
    const mode = uiSettings.value?.workbench_list_mode;
    const patch = {
      focus_mode_enabled: true,
      [FOCUS_MODE_DEFAULT_V2_KEY]: true,
    };
    if (mode !== 'avatars' && mode !== 'hidden' && mode !== 'list') {
      patch.workbench_list_compact = true;
      patch.workbench_list_mode = 'list';
      patch.is_contact_sidebar_open = false;
    }
    if (typeof currentNav === 'number' && currentNav > 56) {
      patch.workbench_nav_width_before_focus = currentNav;
    }
    patch.sidebar_width = 56;
    updateUISettings(patch);
  };

  // O hub passou a ser a porta da navegação, e a fila precisa abrir na tela
  // normal. Quem já tinha sido levado à lista escondida ou ao rail de avatares
  // pela migração anterior volta uma vez para a lista; a imersão continua a um
  // clique no cluster arrastável, e a escolha manual persiste a partir daí.
  const applyListDefaultV3 = () => {
    if (isWorkspaceV2.value) return;
    if (uiSettings.value?.[FOCUS_LIST_DEFAULT_V3_KEY]) return;
    const patch = { [FOCUS_LIST_DEFAULT_V3_KEY]: true };
    if (uiSettings.value?.workbench_list_mode !== 'list') {
      patch.workbench_list_mode = 'list';
    }
    updateUISettings(patch);
  };

  // A forma do cluster virou vertical, mas a posição salva é da era
  // horizontal. Só quem de fato tapa cartão, fila ou busca do hub volta
  // ao canto. Sem viewport desktop, não marca: tenta de novo depois.
  const applyDockCollisionV4 = viewport => {
    if (isWorkspaceV2.value) return;
    if (uiSettings.value?.[FOCUS_DOCK_COLLISION_V4_KEY]) return;

    const raw = uiSettings.value?.focus_mode_dock_position;
    const saved =
      raw &&
      typeof raw.xPct === 'number' &&
      typeof raw.yPct === 'number' &&
      Number.isFinite(raw.xPct) &&
      Number.isFinite(raw.yPct)
        ? { xPct: raw.xPct, yPct: raw.yPct }
        : null;

    if (!saved) {
      updateUISettings({ [FOCUS_DOCK_COLLISION_V4_KEY]: true });
      return;
    }

    const view = viewport || readViewport();
    if (!canEvaluateDockCollision(view)) return;

    const patch = { [FOCUS_DOCK_COLLISION_V4_KEY]: true };
    if (dockCollidesWithHub(saved, view)) {
      patch.focus_mode_dock_position = null;
    }
    updateUISettings(patch);
  };

  const enableFocusMode = () => {
    const currentNav = uiSettings.value?.sidebar_width;
    const guide = focusModeGuide.value;
    const firstActivation = !guide.welcome_seen && guide.entries_count === 0;
    const patch = {
      focus_mode_enabled: true,
      workbench_list_compact: true,
      workbench_list_mode: 'avatars',
      is_contact_sidebar_open: false,
      sidebar_width: 56,
      focus_mode_guide: {
        ...guide,
        entries_count: guide.entries_count + 1,
      },
    };
    if (typeof currentNav === 'number' && currentNav > 56) {
      patch.workbench_nav_width_before_focus = currentNav;
    }
    updateUISettings(patch);
    return firstActivation;
  };

  const disableFocusMode = () => {
    // O layout focado é o único. A preferência antiga é ignorada.
  };

  const toggleFocusMode = () => enableFocusMode();

  const markFocusModeOnboarded = () => {
    if (isFocusModeOnboarded.value) return;
    updateUISettings({ focus_mode_onboarded: true });
  };

  const dockPosition = computed(() => {
    const raw = uiSettings.value?.focus_mode_dock_position;
    if (
      raw &&
      typeof raw.xPct === 'number' &&
      typeof raw.yPct === 'number' &&
      Number.isFinite(raw.xPct) &&
      Number.isFinite(raw.yPct)
    ) {
      return { xPct: raw.xPct, yPct: raw.yPct };
    }
    return null;
  });

  const saveDockPosition = pos => {
    if (!pos || typeof pos.xPct !== 'number' || typeof pos.yPct !== 'number') {
      return;
    }
    updateUISettings({
      focus_mode_dock_position: { xPct: pos.xPct, yPct: pos.yPct },
    });
  };

  const clearDockPosition = () => {
    updateUISettings({ focus_mode_dock_position: null });
  };

  const menuSide = computed(() => {
    const raw = uiSettings.value?.focus_mode_menu_side;
    return FOCUS_MENU_SIDES.includes(raw) ? raw : 'auto';
  });

  const setMenuSide = side => {
    if (!FOCUS_MENU_SIDES.includes(side)) return;
    updateUISettings({ focus_mode_menu_side: side });
  };

  // Cluster recolhido: só o botão principal e a seta ficam na tela. Lembra o
  // último estado entre sessões — quem recolheu quer o espaço de volta, não
  // recolher de novo toda vez.
  const isClusterCollapsed = clusterCollapsed.value;
  const setClusterCollapsed = clusterCollapsed.set;

  return {
    focusModeSetting,
    isFocusModeEnabled,
    applyFocusModeDefaultV2,
    applyListDefaultV3,
    applyDockCollisionV4,
    isFocusModeOnboarded,
    enableFocusMode,
    disableFocusMode,
    toggleFocusMode,
    markFocusModeOnboarded,
    focusModeGuide,
    shouldShowWelcome,
    markWelcomeSeen,
    markTourCompleted,
    markNavV2Seen,
    saveTourStep,
    restartFocusGuide,
    dockPosition,
    saveDockPosition,
    clearDockPosition,
    menuSide,
    setMenuSide,
    isClusterCollapsed,
    setClusterCollapsed,
  };
}
